import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidV4 } from 'uuid';
import { AxiosError, isCancel } from 'axios';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger';
import { useSocket } from '@/hooks/useSocket';
import { useUserWorkflowRole, useWorkflowStore, type GraphUpdatedAtSource } from '@/state/workflow.state';
import { useCreditsContext } from '@/services/CreditsContext';
import { I18N_KEYS } from '@/language/keys';
import { ModelBaseNodeData } from '@/types/nodes/model';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { useUserStore } from '@/state/user.state';
import { SubscriptionType } from '@/types/shared';
import { sanitizeNodesForCost } from '@/components/Nodes/Utils';
import {
  getClientPayloadFailurePhase,
  isOversizedExecutePayloadError,
  serializeExecuteErrorForLog,
  summarizeExecutePayloadSize,
} from '@/utils/execute-payload';
import { prepareBatchDataForRun, type ComputedImageEntry, type PrecedingIteratorData } from '@/utils/flow';
import { useSaveRecipe } from '@/hooks/useSaveRecipe';
import { IGNORE_NODE_TYPES } from '@/consts/node-types.consts';
import { SERVER_ERROR_CODES } from '@/consts/server-error-codes.consts';
import {
  FF_BLOCK_EDITING_ON_FAILING_SAVES,
  FF_BLOCK_FREE_USER_MODELS,
  FF_BLOCK_FREE_USER_VIDEO_MODEL,
  FF_LIGHTWEIGHT_BATCH_COST,
  FF_LOG_SOCKET_EVENTS,
  FF_RESUME_BATCH_POLLING,
  FF_RUN_UPLOAD_STATUS,
  FF_SOCKET_RECONNECTION_ISSUE,
  FF_WEAVE_EXECUTE_COMPUTED_IMAGES,
} from '@/consts/featureFlags';
import { isVideoOutputNode } from '@/utils/model-video.utils';
import { isFreePlanBlockedModelNode } from '@/utils/free-plan-blocked-models.utils';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { getIteratorValues } from '@/utils/iterator.utils';
import { getBatchCostApi } from '@/api/batches/batches.api';
import { getIteratorRunModeTargets, mergeIteratorRunModeTargetsByNodeId } from '@/utils/iterator-run-mode.utils';
import { isModelNode } from '@/components/Nodes/utils/nodeUtils';
import { TERMINAL_WORKFLOW_RUN_STATUSES } from '@/types/batch.types';
import { reportSocketHealth } from '@/utils/socketHealth';
import { awaitPendingDirectLoadUploads } from '../directLoadUploads';
import { useFlowView, useGetEdges, useGetNodes } from '../FlowContext';
import { applyParamOverrides, type RunNodeOptions } from './paramOverrides.utils';
import useUnverifiedModelRunDialog from './useUnverifiedModelRunDialog';
import { usePromptNodeHistoryBatch } from './usePromptNodeHistoryBatch';
import { useSaveHold, type CatchUpOutcome } from './useSaveHold';
import { useBatches, useUpdateBatches } from './batches.store';
import { getBatchStatus, isAnyRun, isBatchCancellable } from './batch.utils';
import { createUploadStatusTracker, type UploadStatusTracker } from './uploadStatus';
import type { ResultDeliveryContext } from './resultDiagnostics';
import type { Edge, IteratorInput, IteratorRunMode, Node } from '@/types/node';
import type { BatchRun, BatchStatus, NodeRun, RecipeRun } from '@/types/batch.types';

const logger = log.getLogger('FlowModelRunContext');

const POLL_INTERVAL = 10000;
/**
 * How long a poll, or the status request after a cancel, waits for a reply. Past it the request fails, so the save hold
 * hears of it; one that never settles would never fail. The catch-up waits as long as it takes: it runs with the socket
 * up, where a failure changes nothing and a late reply can still release a hold.
 */
const STATUS_REQUEST_TIMEOUT_MS = 20_000;

/**
 * How a batch update reached the client. `client_triggered` is an update from the client's own
 * action — the execute response, the local cancel snapshot, the status check after a cancel.
 * `catch_up` is the one-off fetch when the socket connects. Only `socket` counts toward socket health.
 */
type BatchUpdateSource = 'socket' | 'poll' | 'client_triggered' | 'catch_up';

const GRAPH_UPDATED_AT_SOURCE: Record<BatchUpdateSource, GraphUpdatedAtSource> = {
  socket: 'batch_socket',
  poll: 'batch_poll',
  catch_up: 'batch_catch_up',
  client_triggered: 'batch_request',
};

/**
 * Whether a batch has finished, judged by the incoming parent status when the batch has one, even
 * if its runs disagree; otherwise the batch is done once no run is RUNNING.
 */
const isBatchInTerminalState = (incoming: BatchRun, merged: BatchRun): boolean =>
  incoming.status ? TERMINAL_WORKFLOW_RUN_STATUSES.includes(incoming.status) : !isAnyRun(merged, 'RUNNING');

/**
 * Decides whether a finished run counts as a socket-health sample. Returns `null` when it does, or
 * the reason it is reported on the excluded event instead.
 *
 * Analytics only: it picks which socket-health event a run is logged under and never affects
 * polling, delivery, or what the user sees.
 *
 * A run counts only if this page started it and its final state had to reach the client over the
 * socket or the poll. Otherwise its `socketMessages` would not reflect the socket's health:
 * - `inherited` — this page didn't start the run (it was in flight across a reload, or a
 *   collaborator started it), so the messages before this page loaded were never counted.
 * - `canceled_here` — the user canceled on this page, and the local cancel snapshot ended the run
 *   before the socket had anything to deliver.
 * - `terminal_on_execute` — the execute response was already terminal, so nothing was left to
 *   deliver.
 *
 * Counting those last two would add runs with zero socket messages that only look like a dead socket.
 */
const getSocketHealthExclusion = (
  startedHere: boolean,
  terminalSource: BatchUpdateSource,
  outcome: BatchStatus,
): 'inherited' | 'canceled_here' | 'terminal_on_execute' | null => {
  if (!startedHere) return 'inherited';
  if (terminalSource !== 'client_triggered') return null;
  return outcome === 'CANCELED' ? 'canceled_here' : 'terminal_on_execute';
};

/**
 * Finds the convergence targets that need iterator config for this run.
 * The same target can be discovered through multiple selected nodes, so merge
 * by target node before writing iteratorInput into the request payload.
 */
const getIterationConfigTargetsByNodeId = (selectedNodeIds: string[], preparedNodes: Node[], preparedEdges: Edge[]) => {
  const selectedNodeIdsInBatch = new Set(selectedNodeIds);
  const targets = selectedNodeIds.flatMap((nodeId) =>
    getIteratorRunModeTargets(nodeId, preparedNodes, preparedEdges, selectedNodeIdsInBatch),
  );

  return new Map(mergeIteratorRunModeTargetsByNodeId(targets).map((target) => [target.nodeId, target.iterators]));
};

const buildIteratorInputForTargetNode = (
  node: Node,
  precedingIterators: PrecedingIteratorData[],
  runMode: IteratorRunMode,
  listSelectorOptions: (nodeId: string) => string[],
): IteratorInput | null => {
  const iteratorIds = new Set(precedingIterators.map((iterator) => iterator.iteratorNode.id));
  if (iteratorIds.size < 2) {
    return null;
  }

  const iteratorInput = {
    ...node.data.iteratorInput,
    runMode,
  } as IteratorInput;

  precedingIterators.forEach((iterator) => {
    const values = getIteratorValues(iterator.iteratorNode.id, iterator.iteratorNode.data, listSelectorOptions);
    iteratorInput[iterator.inputKey] = { iteratorNodeId: iterator.iteratorNode.id, options: values };
  });

  return iteratorInput;
};

const isIgnoreNodeType = (nodeType: string | undefined): nodeType is (typeof IGNORE_NODE_TYPES)[number] => {
  return !!nodeType && IGNORE_NODE_TYPES.includes(nodeType as (typeof IGNORE_NODE_TYPES)[number]);
};

/**
 * The credits figure a surface actually rendered when Run was pressed. Sent with the execute call
 * purely so the server can compare it against what the batch is billed — the two surfaces show
 * different numbers, so each reports its own rather than a reconstruction.
 */
export interface DisplayedCost {
  credits?: number;
  perSecondRate?: number;
  surface: 'drawer' | 'node_footer' | 'node_result' | 'agent_run_card';
}

interface FlowRunContextType {
  addNodeCallback: (nodeId: string, callback: NodeCallback) => () => void;
  getBatchCost: (
    nodesToRun: Node[],
    numberOfRuns?: number,
    runMode?: IteratorRunMode,
  ) => Promise<{ cost?: number; isDynamicCost?: boolean }>;
  initializingBatchNodes: Record<string, string[]>;
  /** Nodes of runs whose request is still being sent and has shown as "Uploading", keyed like `initializingBatchNodes`. */
  uploadingBatchNodes: Record<string, string[]>;
  /** Stops sending the request of the run that carries this node. Does nothing once the whole request is sent. */
  cancelUpload: (nodeId: string) => void;
  runNextConnectedNodes: () => Promise<string | null>;
  runNode: (
    nodeId: string,
    numberOfRuns?: number,
    runMode?: IteratorRunMode,
    displayedCost?: DisplayedCost,
    options?: RunNodeOptions,
  ) => Promise<string | null>;
  runNodes: (
    nodesToRun: Node[],
    numberOfRuns?: number,
    runMode?: IteratorRunMode,
    displayedCost?: DisplayedCost,
  ) => Promise<string | null>;
  runSelected: (numberOfRuns?: number, runMode?: IteratorRunMode) => Promise<string | null>;
  stopBatch: (batchId: string) => Promise<void>;
}

type NodeCallback = (batch: BatchRun, recipeRun: RecipeRun, nodeRun: NodeRun, delivery?: ResultDeliveryContext) => void;

const FlowRunContext = React.createContext<FlowRunContextType | null>(null);
export const StorybookFlowRunContext = FlowRunContext;

export function FlowRunProvider({ children, recipeId }: { children: React.ReactNode; recipeId: string }) {
  const updateBatches = useUpdateBatches();
  const batches = useBatches();
  const { addListener, getIsConnected: getIsSocketConnected, recreateSocket } = useSocket();
  const { setUserCredits, setWorkspaceCredits } = useCreditsContext();
  const nodeCallbacks = useRef<Record<string, NodeCallback>>({});
  const batchPollIntervals = useRef<Record<string, number>>({});
  /** Batches already attributed, so a late duplicate of a terminal update is not counted twice. */
  const attributedBatches = useRef<Set<string>>(new Set());
  /** Socket messages received per batch. See the terminal report for how to read them. */
  const socketMessageCounts = useRef<Record<string, number>>({});
  /**
   * Batches this page started. Anything else was inherited — in flight across a reload
   * so its socket history is partial and is reported apart from the main path.
   */
  const startedBatches = useRef<Set<string>>(new Set());
  const getNodes = useGetNodes();
  const getEdges = useGetEdges();
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const [initializingBatchNodes, setInitializingBatchNodes] = useState<Record<string, string[]>>({});
  const [uploadingBatchNodes, setUploadingBatchNodes] = useState<Record<string, string[]>>({});
  const uploadsInFlight = useRef<Record<string, { controller: AbortController; tracker: UploadStatusTracker }>>({});
  const flowView = useFlowView();
  const saveRecipe = useSaveRecipe();
  const FF_isSocketReconnectionIssue = useFeatureFlagEnabled(FF_SOCKET_RECONNECTION_ISSUE);
  const FF_isLogSocketEvents = useFeatureFlagEnabled(FF_LOG_SOCKET_EVENTS);
  const { value: FF_isResumeBatchPolling } = useFeatureFlag(FF_RESUME_BATCH_POLLING);
  const FF_isResumeBatchPollingRef = useRef(FF_isResumeBatchPolling);
  FF_isResumeBatchPollingRef.current = FF_isResumeBatchPolling;
  /**
   * Aborted on leaving the workflow, so a status response still in flight can't write the old
   * workflow's batch back into the store.
   */
  const statusRequestsAbort = useRef(new AbortController());
  /**
   * A guest tab — a viewer, or an editor whose editing is blocked — asks for no batch status: nothing it
   * receives is saved from it, and a blocked tab only gets out of its block through a refresh. A viewer
   * sees a run as it was when the page loaded.
   */
  const isBlockEditingOnFailingSavesEnabled = useFeatureFlag(FF_BLOCK_EDITING_ON_FAILING_SAVES).value === true;
  const role = useUserWorkflowRole();
  const isStatusStopped = isBlockEditingOnFailingSavesEnabled && role === 'guest';
  const isBlockEditingOnFailingSavesEnabledRef = useRef(isBlockEditingOnFailingSavesEnabled);
  isBlockEditingOnFailingSavesEnabledRef.current = isBlockEditingOnFailingSavesEnabled;
  const isStatusStoppedRef = useRef(isStatusStopped);
  isStatusStoppedRef.current = isStatusStopped;
  const { shouldCapturePromptNodeHistory, refreshHistoryOnBatchTerminal, captureAfterBatchExecute } =
    usePromptNodeHistoryBatch(recipeId);
  const { handleUnverifiedModelError } = useUnverifiedModelRunDialog();
  const { value: isBlockFreeUserVideoModelEnabled } = useFeatureFlag(FF_BLOCK_FREE_USER_VIDEO_MODEL);
  const hasActiveWeaveMcpPromoCredits = useUserStore(
    (state) => state.user?.activeWorkspace?.hasActiveWeaveMcpPromoCredits === true,
  );
  const { value: isBlockFreeUserModelsEnabled } = useFeatureFlag(FF_BLOCK_FREE_USER_MODELS);
  // Missing flag stays on (crash fix). PostHog 0% or `featureFlagsOff=` reverts to the old /cost path.
  const isLightweightBatchCostEnabled = useFeatureFlag(FF_LIGHTWEIGHT_BATCH_COST).value !== false;
  // Missing flag stays off so a new SPA still posts inline image urls until host-once is on the API.
  const { value: isExecuteComputedImagesEnabled } = useFeatureFlag(FF_WEAVE_EXECUTE_COMPUTED_IMAGES);
  const { value: isRunUploadStatusEnabled } = useFeatureFlag(FF_RUN_UPLOAD_STATUS);
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const showUploadStatus = isRunUploadStatusEnabled === true && !isFigmaEmbedMode;

  const setNodeValidation = useWorkflowStore((state) => state.setNodeValidation);
  const mergeResolvedAssets = useWorkflowStore((state) => state.mergeResolvedAssets);
  const adoptGraphUpdatedAt = useWorkflowStore((state) => state.adoptGraphUpdatedAt);

  const addNodeCallback = useCallback(
    (nodeId: string, callback: NodeCallback) => {
      nodeCallbacks.current[nodeId] = callback;
      // If the current batches are already running call
      const runningBatches = Object.values(batches).filter((batch) =>
        batch.recipeRuns.some((run) => run.status === 'RUNNING'),
      );
      runningBatches.forEach((batch) => {
        const runningWorkflowRunIds = batch.recipeRuns
          .filter((item) => item.status === 'RUNNING')
          .map((item) => item.id);
        batch.recipeRuns.forEach((run) => {
          run.nodeRuns.forEach((nodeRun) => {
            if (nodeRun.nodeId === nodeId) {
              callback(batch, run, nodeRun, {
                source: 'cached_registration',
                runningWorkflowRunIds: runningWorkflowRunIds.slice(0, 50),
                runningWorkflowRunCount: runningWorkflowRunIds.length,
              });
            }
          });
        });
      });
      return () => {
        delete nodeCallbacks.current[nodeId];
      };
    },
    [batches],
  );

  /**
   * The save hold's callbacks, set below once the hook exists. `onBatchUpdate` notes each update before writing
   * it into the batch store, so a run that just started already counts as caught up when that store change
   * reaches the hold, and isn't held.
   */
  const noteBatchUpdateRef = useRef<(batch: BatchRun, source: ResultDeliveryContext['source']) => void>(() => {});
  const noteBatchUpdateFailedRef = useRef<(batchId: string) => void>(() => {});

  const onBatchUpdate = useCallback(
    (
      data: BatchRun,
      source: BatchUpdateSource,
      deliverySource: ResultDeliveryContext['source'] = source === 'client_triggered' ? 'unknown' : source,
    ) => {
      // The cancel snapshot is the client's own guess, not news from the server.
      if (deliverySource !== 'cancel') noteBatchUpdateRef.current(data, deliverySource);
      if (source === 'socket') {
        socketMessageCounts.current[data.batchId] = (socketMessageCounts.current[data.batchId] ?? 0) + 1;
      }
      mergeResolvedAssets(data.resolvedAssets ?? {});
      // Before the node callbacks apply the results, so the save they trigger is not rejected for the server
      // having written those results into the workflow.
      if (data.graphUpdatedAt) {
        adoptGraphUpdatedAt(data.recipeId, data.graphUpdatedAt, GRAPH_UPDATED_AT_SOURCE[source]);
      }
      data.recipeRuns.forEach((recipeRun) => {
        if (recipeRun.nodeRuns.every((nodeRun) => nodeRun.status === 'COMPLETED') && recipeRun.status === 'RUNNING') {
          recipeRun.status = 'COMPLETED';
        }
      });
      const mergedBatches = updateBatches({ [data.batchId]: data });
      const mergedBatch = mergedBatches[data.batchId];
      // Stop polling on terminal.
      const isTerminal = isBatchInTerminalState(data, mergedBatch);
      if (isTerminal && data.batchId in batchPollIntervals.current) {
        clearInterval(batchPollIntervals.current[data.batchId]);
        delete batchPollIntervals.current[data.batchId];
      }
      if (isTerminal) {
        // Semi-quantitative socket health: read these as shares across many runs, not per-run
        // verdicts. Socket and poll run in parallel, so the numbers carry polling noise.
        //
        // `socketMessages` counts receipts, not useful deliveries. Every update is a full snapshot,
        // so a message can repeat known state or carry a regression the merge discards. Read it as
        // zero versus non-zero: zero means the socket was silent for the whole run.
        //
        // `completedBy` is the path whose update finished the run. A healthy socket still loses the
        // race when a poll tick lands between the server finishing and the push arriving — roughly
        // socket lag over POLL_INTERVAL, about 1% at 100ms — so `poll` is only signal well above that
        // baseline. Above it, with `socketMessages > 0`, the socket died or stalled mid-run; it can
        // also mean a server path that ends a run without emitting, so split by `outcome` first.
        if (FF_isLogSocketEvents && !attributedBatches.current.has(data.batchId)) {
          attributedBatches.current.add(data.batchId);
          const outcome = getBatchStatus(mergedBatch);
          const exclusion = getSocketHealthExclusion(startedBatches.current.delete(data.batchId), source, outcome);
          reportSocketHealth(exclusion ? 'batch_socket_delivery_excluded' : 'batch_socket_delivery', {
            batchId: data.batchId,
            socketMessages: socketMessageCounts.current[data.batchId] ?? 0,
            outcome,
            completedBy: source,
            ...(exclusion ? { reason: exclusion } : {}),
          });
          delete socketMessageCounts.current[data.batchId];
        }
        refreshHistoryOnBatchTerminal(data.batchId);
      }
      if (data.userRemainingCredits !== undefined) {
        setUserCredits(data.userRemainingCredits);
      }
      if (typeof data.remainingCredits === 'number') {
        setWorkspaceCredits(data.remainingCredits);
      }
      const callbackKeys = Object.keys(nodeCallbacks.current);
      mergedBatch.recipeRuns.forEach((recipeRun) => {
        recipeRun.nodeRuns.forEach((nodeRun) => {
          if (!callbackKeys.includes(nodeRun.nodeId)) {
            return;
          }
          nodeCallbacks.current[nodeRun.nodeId]?.(mergedBatch, recipeRun, nodeRun, { source: deliverySource });
        });
      });
    },
    [
      FF_isLogSocketEvents,
      updateBatches,
      setUserCredits,
      setWorkspaceCredits,
      refreshHistoryOnBatchTerminal,
      mergeResolvedAssets,
      adoptGraphUpdatedAt,
    ],
  );

  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return addListener('batch_run_status', (data) => {
      onBatchUpdate(data, 'socket');
    });
  }, [addListener, onBatchUpdate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        // eslint-disable-next-line react-hooks-js/exhaustive-deps
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Clear all intervals when the recipeId changes
    return () => {
      Object.values(batchPollIntervals.current).forEach((interval) => {
        clearInterval(interval);
      });
      batchPollIntervals.current = {};
      if (FF_isResumeBatchPollingRef.current) {
        statusRequestsAbort.current.abort();
        statusRequestsAbort.current = new AbortController();
      }
    };
  }, [recipeId]);

  const prepareBatchDataForRunWithContext = useCallback(
    (
      nodesToRun: Node[],
      options?: { snapshotModelInputs?: boolean; sendComputedImageRefs?: boolean },
    ): { nodes: Node[]; edges: Edge[]; hasIterators: boolean; computedImages?: ComputedImageEntry[] } => {
      const nodes = getNodes();
      const edges = getEdges();
      const snapshotModelInputs = options?.snapshotModelInputs ?? true;
      if (!snapshotModelInputs) {
        return prepareBatchDataForRun(nodesToRun, nodes, edges);
      }
      const sendComputedImageRefs = options?.sendComputedImageRefs === true;
      const computedImageCache = new Map<string, ComputedImageEntry>();
      const prepared = prepareBatchDataForRun(nodesToRun, nodes, edges, (nodeId, nodeData, payloadNodeIds) =>
        flowView.updateInputBeforeRun(nodeId, nodeData, computedImageCache, sendComputedImageRefs, payloadNodeIds),
      );
      return {
        ...prepared,
        computedImages: sendComputedImageRefs ? [...computedImageCache.values()] : undefined,
      };
    },
    [getEdges, getNodes, flowView],
  );

  const applyIteratorConfigToNodes = useCallback(
    (preparedNodes: Node[], preparedEdges: Edge[], selectedNodeIds: string[], runMode?: IteratorRunMode): Node[] => {
      if (!runMode) {
        return preparedNodes;
      }
      const targetsByNodeId = getIterationConfigTargetsByNodeId(selectedNodeIds, preparedNodes, preparedEdges);

      return preparedNodes.map((node) => {
        const targetIterators = targetsByNodeId.get(node.id);
        if (!targetIterators) {
          return node;
        }

        const iteratorInput = buildIteratorInputForTargetNode(node, targetIterators, runMode, (nodeId) =>
          flowView.listSelectorOptions(nodeId),
        );
        if (!iteratorInput) {
          return node;
        }

        return {
          ...node,
          data: {
            ...node.data,
            iteratorInput,
          },
        };
      });
    },
    [flowView],
  );

  const statusReq = useCallback(
    async (batchId: string, { retry = true, timeout }: { retry?: boolean; timeout?: number } = {}) => {
      return safeAxios.get<BatchRun>(API_ROUTES.batches.status({ recipeId, batchId }), {
        signal: statusRequestsAbort.current.signal,
        ...(!retry && { 'axios-retry': { retries: 0 } }),
        ...(timeout !== undefined && { timeout }),
      });
    },
    [recipeId],
  );

  /** A catch-up can settle after the canvas unmounts; applying it then would write into the next workflow's stores. */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const catchUpBatch = useCallback(
    async (batchId: string): Promise<CatchUpOutcome> => {
      if (isStatusStoppedRef.current) return 'canceled';
      try {
        const { data } = await statusReq(batchId);
        if (!isMountedRef.current) return 'canceled';
        onBatchUpdate(data, 'catch_up');
        return isBatchInTerminalState(data, data) ? 'finished' : 'running';
      } catch (error) {
        if (isCancel(error)) return 'canceled';
        // Debug, so an outage doesn't send one per batch per tab; `workflow_socket_catch_up` counts them.
        logger.debug(`Could not catch up batch ${batchId}`, error);
        noteBatchUpdateFailedRef.current(batchId);
        return 'failed';
      }
    },
    [statusReq, onBatchUpdate],
  );

  const saveAfterHold = useCallback(() => saveRecipe({ saveFlowId: 'save_hold_release' }, true), [saveRecipe]);
  const isBatchPolled = useCallback((batchId: string) => batchId in batchPollIntervals.current, []);
  const { noteBatchUpdate, noteBatchUpdateFailed, noteBatchCanceled } = useSaveHold({
    workflowId: recipeId,
    catchUpBatch,
    saveWorkflow: saveAfterHold,
    isBatchPolled,
  });
  noteBatchUpdateRef.current = noteBatchUpdate;
  noteBatchUpdateFailedRef.current = noteBatchUpdateFailed;

  /** Idempotent: a batch already being polled is left alone, so resuming cannot double up. */
  const startPollingBatch = useCallback(
    (batchId: string) => {
      if (isStatusStoppedRef.current || batchId in batchPollIntervals.current) return;
      batchPollIntervals.current[batchId] = window.setInterval(() => {
        const isHoldEnabled = isBlockEditingOnFailingSavesEnabledRef.current;
        // The next tick tries again, so retrying at once only multiplies the load on a struggling server.
        statusReq(batchId, { retry: !isHoldEnabled, timeout: isHoldEnabled ? STATUS_REQUEST_TIMEOUT_MS : undefined })
          .then((res) => onBatchUpdate(res.data, 'poll'))
          .catch((error) => {
            if (isCancel(error)) return;
            noteBatchUpdateFailedRef.current(batchId);
            logger.error(`Could not poll batch ${batchId}`, error);
          });
      }, POLL_INTERVAL);
    },
    [statusReq, onBatchUpdate],
  );

  // A reload drops `batchPollIntervals`, but the page rehydrates `recipe.batchRuns`, so a run that
  // was in flight comes back with no poll behind it and the socket as its only delivery path. Give
  // every unfinished batch a poll, whoever started it.
  useEffect(() => {
    if (!FF_isResumeBatchPolling || isStatusStopped) return;
    Object.values(batches).forEach((batch) => {
      // The store outlives navigation, and the poll URL is built from this page's recipe.
      if (batch.recipeId !== recipeId) return;
      // A rehydrated batch is its own latest update.
      if (!isBatchInTerminalState(batch, batch)) {
        startPollingBatch(batch.batchId);
      }
    });
  }, [FF_isResumeBatchPolling, isStatusStopped, batches, recipeId, startPollingBatch]);

  useEffect(() => {
    if (!isStatusStopped) return;
    const polledBatches = Object.keys(batchPollIntervals.current).length;
    if (polledBatches > 0) {
      reportSocketHealth('workflow_status_stopped', {
        workflowId: recipeId,
        polledBatches,
        workflowError: useWorkflowStore.getState().workflowError ?? null,
      });
    }
    Object.values(batchPollIntervals.current).forEach((interval) => clearInterval(interval));
    batchPollIntervals.current = {};
    statusRequestsAbort.current.abort();
    statusRequestsAbort.current = new AbortController();
  }, [isStatusStopped, recipeId]);

  const runBatch = useCallback(
    async (
      requestedNodes: Node[],
      numberOfRuns = 1,
      runMode?: IteratorRunMode,
      displayedCost?: DisplayedCost,
      options?: RunNodeOptions,
    ) => {
      // Video models without MCP promo credits and Free-plan blocked models require a paid plan. Drop them from the batch
      // so any run path (run-selected, run-next, a future run-all) can't trigger them — the per-node UI and
      // server enforce the same block. If that leaves nothing to run, there's nothing to do.
      const isFreePlan = isSubscriptionTypeIn([SubscriptionType.Free]);
      const nodesToRun = requestedNodes.filter(
        (node) =>
          !(
            isFreePlan &&
            isModelNode(node) &&
            ((isBlockFreeUserVideoModelEnabled &&
              !hasActiveWeaveMcpPromoCredits &&
              isVideoOutputNode(node.id, flowView)) ||
              (isBlockFreeUserModelsEnabled && isFreePlanBlockedModelNode(node.data)))
          ),
      );
      if (nodesToRun.length === 0) {
        return null;
      }

      if (!getIsSocketConnected()) {
        if (FF_isLogSocketEvents) {
          reportSocketHealth('run_started_without_socket', { willRecreate: FF_isSocketReconnectionIssue });
        }
        if (FF_isSocketReconnectionIssue) {
          logger.warn('Workflow socket is not connected when trying to run batch. Attempting to recreate socket');
          recreateSocket();
        }
      }

      // Validation
      let isValid = true;
      const initializingNodes = new Set<string>();
      nodesToRun.forEach((node) => {
        initializingNodes.add(node.id);
        const missingRequiredInputs = flowView.missingRequiredInputs(node.id);
        if (missingRequiredInputs.length > 0) {
          isValid = false;
          setNodeValidation(node.id, {
            type: 'missingRequiredInputs',
            extraInfo: { keys: missingRequiredInputs },
          });
        }
      });
      // We validate only when there is one node because the input could be created dynamically in the run
      // We check for isValid because we don't want to validate the inputs if the node is already invalid
      if (nodesToRun.length === 1 && isValid) {
        const node = nodesToRun[0];
        const invalidRequiredInputs = flowView.invalidRequiredInputs(node.id);
        if (invalidRequiredInputs.length > 0) {
          isValid = false;
          setNodeValidation(node.id, {
            type: 'emptyRequiredInputs',
            extraInfo: { keys: invalidRequiredInputs },
          });
        }
      }
      if (!isValid) {
        return null;
      }

      // Mark the nodes as initializing up front so the Run button stays disabled
      // while we wait on in-flight uploads and resource loads below.
      const batchTempId = uuidV4();
      const batchNodeIds = Array.from(initializingNodes).sort();
      let payloadNodes: Node[] = nodesToRun;
      let computedImages: ComputedImageEntry[] | undefined;
      let upload: { controller: AbortController; tracker: UploadStatusTracker } | undefined;
      let uploadStartedAt = 0;

      try {
        setInitializingBatchNodes((prev) => ({ ...prev, [batchTempId]: batchNodeIds }));

        // A direct-load 3D model may still be swapping its temp `local:` url for the
        // real CDN url in the background. Wait for it so the payload we evaluate below
        // never contains an unfetchable `local:` key (which the server can't resolve).
        await awaitPendingDirectLoadUploads();

        // Make sure all resources are loaded before evaluating the inputs for the run
        await flowView.loadResources();

        // Prepare the nodes
        const {
          nodes: updatedNodes,
          hasIterators,
          edges: updatedNodesEdges,
          computedImages: preparedImages,
        } = prepareBatchDataForRunWithContext(nodesToRun, {
          sendComputedImageRefs: isExecuteComputedImagesEnabled === true,
        });
        const preparedNodesWithIteratorConfig = applyParamOverrides(
          applyIteratorConfigToNodes(
            updatedNodes,
            updatedNodesEdges,
            nodesToRun.map((node) => node.id),
            runMode,
          ),
          options?.paramOverrides,
        );
        payloadNodes = preparedNodesWithIteratorConfig;
        computedImages = preparedImages;

        // Analytics
        let modelName = '';
        const modelNodes = preparedNodesWithIteratorConfig.filter((node) => node.isModel);
        if (modelNodes.length) {
          modelName = (modelNodes[0].data as ModelBaseNodeData).model?.name || '';
        }
        track(
          'run_model_start',
          { batchSize: modelNodes.length, numberOfRuns, model: modelName, hasIterators },
          TrackTypeEnum.BI,
        );

        void saveRecipe({
          // For logging purposes
          saveFlowId: 'batch_run-' + batchTempId,
        });

        // Snapshot the graph just before the execute await — after loadResources + prep (so captured
        // nodes carry resolved resources, not stale `local:` urls) but before the request is in flight
        // (so capture records the graph that actually ran, not a later canvas mutation).
        const captureGraph =
          shouldCapturePromptNodeHistory && modelNodes.length ? { nodes: getNodes(), edges: getEdges() } : null;

        if (showUploadStatus) {
          upload = {
            controller: new AbortController(),
            tracker: createUploadStatusTracker({
              onShow: () => setUploadingBatchNodes((prev) => ({ ...prev, [batchTempId]: batchNodeIds })),
              onHide: () =>
                setUploadingBatchNodes((prev) => {
                  const { [batchTempId]: _, ...rest } = prev;
                  return rest;
                }),
            }),
          };
          uploadsInFlight.current[batchTempId] = upload;
        }
        uploadStartedAt = Date.now();
        const response = await safeAxios.post<BatchRun>(
          API_ROUTES.batches.execute({ recipeId }),
          {
            numberOfRuns,
            nodes: preparedNodesWithIteratorConfig,
            edges: updatedNodesEdges,
            ...(computedImages?.length ? { computedImages } : {}),
            // `credits` stays undefined when a surface had no price on screen — JSON drops it, so
            // the server can tell "showed nothing" from "showed free".
            ...(displayedCost ? { displayedCost } : {}),
          },
          {
            'axios-retry': { retries: 0 },
            signal: upload?.controller.signal,
            onUploadProgress: upload?.tracker.onUploadProgress,
          },
        );
        // Registered before the first update, so a run that is already terminal in the execute
        // response still reports on the main path.
        startedBatches.current.add(response.data.batchId);
        onBatchUpdate(response.data, 'client_triggered', 'execute');
        if (captureGraph) {
          // Capture is automatic, not a user action — no BI event (server metric covers it).
          captureAfterBatchExecute({
            batchId: response.data.batchId,
            modelNodes,
            preparedRunNodes: preparedNodesWithIteratorConfig,
            nodes: captureGraph.nodes,
            edges: captureGraph.edges,
            numberOfRuns,
          });
        }
        startPollingBatch(response.data.batchId);
        setInitializingBatchNodes((prev) => {
          const { [batchTempId]: _, ...rest } = prev;
          return rest;
        });
        return response.data.batchId;
      } catch (error) {
        if (upload && isCancel(error)) {
          logger.info('Run upload cancelled', {
            workflowId: recipeId,
            elapsedMs: Date.now() - uploadStartedAt,
            ...upload.tracker.getProgress(),
          });
          setInitializingBatchNodes((prev) => {
            const { [batchTempId]: _, ...rest } = prev;
            return rest;
          });
          return null;
        }
        const oversized = isOversizedExecutePayloadError(error);
        const snapshotCompleted = payloadNodes !== nodesToRun;
        logger.error(oversized ? 'Execute payload too large' : 'Could not run batch', {
          reason: oversized ? 'execute_payload_too_large' : 'execute_failed',
          phase: getClientPayloadFailurePhase(error, snapshotCompleted),
          snapshotCompleted,
          recipeId,
          numberOfRuns,
          ...serializeExecuteErrorForLog(error),
          ...summarizeExecutePayloadSize(payloadNodes, { computedImages }),
        });
        setInitializingBatchNodes((prev) => {
          const { [batchTempId]: _, ...rest } = prev;
          return rest;
        });
        if (oversized) {
          throw new Error(t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.REQUEST_TOO_LARGE));
        }
        if (error instanceof AxiosError) {
          if (error.response?.data?.internalErrorCode === SERVER_ERROR_CODES.UNVERIFIED_MODEL_NOT_APPROVED) {
            if (isSubscriptionTypeIn([SubscriptionType.Enterprise])) {
              throw new Error('Unverified model run error');
            }
            return handleUnverifiedModelError(error);
          }
          throw new Error(error.response?.data?.message || t(I18N_KEYS.GENERAL.UNKNOWN_ERROR));
        }

        if (error instanceof Error) {
          throw error;
        }
        throw new Error(t(I18N_KEYS.GENERAL.UNKNOWN_ERROR));
      } finally {
        if (upload) {
          upload.tracker.dispose();
          delete uploadsInFlight.current[batchTempId];
        }
      }
    },
    [
      FF_isLogSocketEvents,
      getIsSocketConnected,
      prepareBatchDataForRunWithContext,
      startPollingBatch,
      track,
      FF_isSocketReconnectionIssue,
      shouldCapturePromptNodeHistory,
      captureAfterBatchExecute,
      recreateSocket,
      flowView,
      setNodeValidation,
      saveRecipe,
      recipeId,
      onBatchUpdate,
      t,
      handleUnverifiedModelError,
      isSubscriptionTypeIn,
      applyIteratorConfigToNodes,
      isBlockFreeUserVideoModelEnabled,
      hasActiveWeaveMcpPromoCredits,
      isBlockFreeUserModelsEnabled,
      isExecuteComputedImagesEnabled,
      showUploadStatus,
      getNodes,
      getEdges,
    ],
  );

  const cancelUpload = useCallback(
    (nodeId: string) => {
      const batchTempId = Object.keys(uploadingBatchNodes).find((id) => uploadingBatchNodes[id].includes(nodeId));
      const upload = batchTempId ? uploadsInFlight.current[batchTempId] : undefined;
      // Once the whole request is sent the server may already be creating the run. Aborting then would
      // drop its answer and leave a charged run the user never sees, so only abort while still sending.
      if (upload?.tracker.isSending()) {
        upload.controller.abort();
      }
    },
    [uploadingBatchNodes],
  );

  const stopBatch = useCallback(
    async (batchId: string) => {
      logger.info(`Stopping batch ${batchId}`);
      const batch = batches[batchId];
      if (!batch) {
        logger.error(`Batch ${batchId} not found`);
        return;
      }
      if (!isBatchCancellable(batch)) {
        logger.error(`Batch ${batchId} is not running or pending`);
        return;
      }
      try {
        await safeAxios.post(API_ROUTES.batches.cancel({ recipeId: batch.recipeId, batchId }));
        const awaitsServer = noteBatchCanceled(batchId);
        const canceledBatch: BatchRun = {
          ...batch,
          status: 'CANCELED',
          recipeRuns: batch.recipeRuns.map((run) => ({
            ...run,
            status: 'CANCELED',
            nodeRuns: run.nodeRuns.map((nodeRun) => ({ ...nodeRun, status: 'CANCELED' })),
          })),
        };
        onBatchUpdate(canceledBatch, 'client_triggered', 'cancel');
        if (batchId in batchPollIntervals.current) {
          clearInterval(batchPollIntervals.current[batchId]);
          delete batchPollIntervals.current[batchId];
        }
        try {
          // Do 1 extra status req to get updated credits
          const res = await statusReq(batchId, {
            timeout: isBlockEditingOnFailingSavesEnabledRef.current ? STATUS_REQUEST_TIMEOUT_MS : undefined,
          });
          onBatchUpdate(res.data, 'client_triggered', 'cancel_refresh');
        } catch (error) {
          // Aborted by leaving the workflow or the tab turning guest; nothing is left to follow up.
          if (isCancel(error)) return;
          logger.warn(`Failed to send extra status request for updated credits`, error, { batchId });
          noteBatchUpdateFailedRef.current(batchId);
          // Saves stay held until the server's state arrives; keep asking, as for a running batch.
          if (awaitsServer) startPollingBatch(batchId);
        }
      } catch (error) {
        logger.error(`Could not cancel batch ${batchId}`, error);
      }
    },
    [batches, noteBatchCanceled, onBatchUpdate, startPollingBatch, statusReq],
  );

  const getBatchCost = useCallback(
    async (nodesToRun: Node[], numberOfRuns = 1, runMode?: IteratorRunMode) => {
      // Cost prices from model + params + iterator topology. Snapshotting computed
      // images (exportNodeRender) is execute-only and OOMs iterator + graphics graphs.
      let costNodes: Node[] = nodesToRun;
      try {
        const { nodes: updatedNodes, edges: updatedNodesEdges } = prepareBatchDataForRunWithContext(nodesToRun, {
          snapshotModelInputs: !isLightweightBatchCostEnabled,
        });
        const updatedNodesWithRunMode = applyIteratorConfigToNodes(
          updatedNodes,
          updatedNodesEdges,
          nodesToRun.map((node) => node.id),
          runMode,
        );
        costNodes = isLightweightBatchCostEnabled
          ? sanitizeNodesForCost(updatedNodesWithRunMode)
          : updatedNodesWithRunMode;

        return await getBatchCostApi({
          recipeId,
          numberOfRuns,
          nodes: costNodes,
          edges: updatedNodesEdges,
        });
      } catch (error) {
        const oversized = isOversizedExecutePayloadError(error);
        const snapshotCompleted = costNodes !== nodesToRun;
        logger.error(oversized ? 'Cost payload too large' : 'Could not get batch cost', {
          reason: oversized ? 'cost_payload_too_large' : 'cost_failed',
          phase: getClientPayloadFailurePhase(error, snapshotCompleted),
          snapshotCompleted,
          recipeId,
          numberOfRuns,
          ...serializeExecuteErrorForLog(error),
          ...summarizeExecutePayloadSize(costNodes),
        });
        if (oversized) {
          throw new Error(t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.REQUEST_TOO_LARGE));
        }
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(t(I18N_KEYS.GENERAL.UNKNOWN_ERROR));
      }
    },
    [applyIteratorConfigToNodes, isLightweightBatchCostEnabled, prepareBatchDataForRunWithContext, recipeId, t],
  );

  const runSelected = useCallback(
    async (numberOfRuns = 1, runMode?: IteratorRunMode) => {
      const selectedNodes = getNodes().filter((node) => node.selected && !isIgnoreNodeType(node.type));
      return await runBatch(selectedNodes, numberOfRuns, runMode);
    },
    [getNodes, runBatch],
  );

  const runNextConnectedNodes = useCallback(async () => {
    const nodes = getNodes();
    const edges = getEdges();
    const selectedNodes = nodes.filter((node) => node.selected);
    if (selectedNodes.length !== 1) {
      return null;
    }

    const selectedNode = selectedNodes[0];

    let nodesToRun: Node[];
    if (selectedNode.isModel) {
      nodesToRun = [selectedNode];
    } else {
      const connectingEdges = edges.filter((edge) => edge.source === selectedNode.id);
      nodesToRun = nodes.filter((node) => connectingEdges.some((edge) => edge.target === node.id));
    }

    return await runBatch(nodesToRun);
  }, [getEdges, getNodes, runBatch]);

  const runNode = useCallback(
    async (
      nodeId: string,
      numberOfRuns = 1,
      runMode?: IteratorRunMode,
      displayedCost?: DisplayedCost,
      options?: RunNodeOptions,
    ) => {
      const node = getNodes().find((node) => node.id === nodeId);
      if (!node) {
        return null;
      }
      return await runBatch([node], numberOfRuns, runMode, displayedCost, options);
    },
    [getNodes, runBatch],
  );

  const runNodes = useCallback(
    async (nodesToRun: Node[], numberOfRuns = 1, runMode?: IteratorRunMode, displayedCost?: DisplayedCost) => {
      const filteredNodes = nodesToRun.filter((node) => !isIgnoreNodeType(node.type));
      return await runBatch(filteredNodes, numberOfRuns, runMode, displayedCost);
    },
    [runBatch],
  );

  return (
    <FlowRunContext.Provider
      value={{
        addNodeCallback,
        getBatchCost,
        initializingBatchNodes,
        uploadingBatchNodes,
        cancelUpload,
        runNextConnectedNodes,
        runNode,
        runNodes,
        runSelected,
        stopBatch,
      }}
    >
      {children}
    </FlowRunContext.Provider>
  );
}

export const useFlowRunContext = () => {
  const context = useContext(FlowRunContext);
  if (!context) {
    throw new Error('useFlowRunContext must be used within a FlowRunProvider');
  }
  return context;
};
